import { PALETTE } from "./palette.ts";

/**
 * What a colour is *for*, in both themes — the layer every surface actually paints with.
 *
 * **Resolved values, never `var(--x)`.** On the web a role can point at a palette entry and
 * let the cascade finish the job; React Native has no custom properties and no cascade, so
 * a mobile surface needs the answer rather than the reference. Writing them out here is
 * what makes one definition serve both, and it is why this package exists at all
 * ([ADR 0025](../../../docs/adr/0025-the-mobile-surface.md) decision 8).
 *
 * **A total `Record` over both themes**, the repo's idiom (`STATED_WRITE_CHANNELS`,
 * `ACTION_RETENTION`): a seventh role cannot be added without answering for it in the dark
 * theme too, which is the half that gets forgotten — and the half that produces a screen
 * nobody can read at night.
 */
export type BrandTheme = "light" | "dark";

/**
 * The roles, and each one is here because a surface would otherwise invent it.
 *
 * `onBrand` earns its place most obviously: `brand` inverts between forest and sage, and
 * off-white text on sage does not carry. A surface that hard-codes the light answer ships a
 * dark theme with unreadable buttons, and nothing about that fails a build.
 */
export type BrandRole =
  | "bg"
  | "surface"
  | "ink"
  | "inkSoft"
  | "accent"
  | "brand"
  | "rule"
  | "muted"
  | "onBrand"
  | "scrim"
  | "lift";

/**
 * `scrim` and `lift` are the two that are not plain colours: a translucent backdrop and a
 * box-shadow. They are here anyway, because they are brand decisions rather than layout
 * ones and a second surface would otherwise pick its own — but **`lift` is a CSS shadow and
 * a native surface has to translate it** (`shadowColor` / `shadowOffset` / `elevation`), so
 * it is the one token that cannot be used verbatim on both platforms.
 */
export const BRAND_ROLES: Readonly<Record<BrandTheme, Readonly<Record<BrandRole, string>>>> = {
  light: {
    bg: PALETTE.offWhite,
    surface: "#fffdf8",
    ink: PALETTE.navy,
    inkSoft: "#46586b",
    accent: PALETTE.copper,
    brand: PALETTE.forest,
    rule: "#e2dccc",
    muted: "#f1efe6",
    onBrand: PALETTE.offWhite,
    scrim: "rgba(13, 27, 42, 0.28)",
    lift: "0 8px 24px rgba(13, 27, 42, 0.06)",
  },
  dark: {
    bg: "#0f1a14",
    surface: "#16241c",
    ink: "#ece7da",
    inkSoft: "#a9b5ab",
    accent: "#e08a4e",
    brand: PALETTE.sage,
    rule: "#26382c",
    muted: "#1c2b22",
    onBrand: "#0f1a14",
    scrim: "rgba(0, 0, 0, 0.5)",
    lift: "0 8px 24px rgba(0, 0, 0, 0.35)",
  },
};

/**
 * The corner radius, as a number rather than `14px`, because the two platforms spell the
 * unit differently and only one of them spells it at all.
 */
export const RADIUS_PX = 14;

/** The `theme-color` every surface publishes — forest, in both themes. */
export const THEME_COLOUR = PALETTE.forest;
